import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { instanceToken } from "../../../utils/constant";
import {
  EditFieldLabel,
  EditPageLayout,
} from "../../../components/edit/EditPageLayout";
import PolicyPdfUpload from "./PolicyPdfUpload";
import {
  deleteDocument,
  DOCUMENT_TYPE_OPTIONS,
  fetchDocument,
  isValidScope,
  LANGUAGE_OPTIONS,
  resolveDocumentType,
  SCOPES,
  updateDocument,
  validatePdfFile,
} from "./policyDocumentUtils";

const CUSTOM_DOC_TYPE = "__custom__";

function matchDocumentTypePreset(value) {
  if (DOCUMENT_TYPE_OPTIONS.includes(value)) return value;
  return CUSTOM_DOC_TYPE;
}

export default function UpdatePolicyDocument({ homeAlert }) {
  const navigate = useNavigate();
  const { scope, id } = useParams();
  const validScope = isValidScope(scope);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [errors, setErrors] = useState({});

  const [documentTypePreset, setDocumentTypePreset] = useState(
    DOCUMENT_TYPE_OPTIONS[0]
  );
  const [customDocumentType, setCustomDocumentType] = useState("");
  const [policyNo, setPolicyNo] = useState("");
  const [language, setLanguage] = useState("en");
  const [pdfFile, setPdfFile] = useState(null);

  useEffect(() => {
    if (!validScope) return;
    const load = async () => {
      try {
        const data = await fetchDocument(scope, instanceToken.token, id);
        setDoc(data);
        if (scope === SCOPES.customer) {
          setPolicyNo(data.policy_no || "");
        }
        const preset = matchDocumentTypePreset(data.document_type);
        setDocumentTypePreset(preset);
        if (preset === CUSTOM_DOC_TYPE) {
          setCustomDocumentType(data.document_type || "");
        }
        setLanguage(data.language === "mm" ? "mm" : "en");
      } catch (error) {
        console.error(error);
        homeAlert("Failed to load policy document.", true);
      } finally {
        setPageLoading(false);
      }
    };
    load();
  }, [scope, id, validScope, homeAlert]);

  const handleRemove = async () => {
    try {
      await deleteDocument(scope, instanceToken.token, id);
      homeAlert("Policy document has been removed.", false);
      navigate(`/policy-documents?scope=${scope}`);
    } catch (error) {
      console.error(error);
      homeAlert("Error on server!", true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = {};
    const useCustom = documentTypePreset === CUSTOM_DOC_TYPE;
    const documentType = resolveDocumentType(
      documentTypePreset,
      customDocumentType,
      useCustom
    );

    if (!documentType) {
      err.documentType = "Document type is required.";
    }
    if (scope === SCOPES.customer && !policyNo.trim()) {
      err.policyNo = "Policy number is required.";
    }
    if (pdfFile) {
      validatePdfFile(pdfFile, err);
    }

    if (Object.keys(err).length) {
      setErrors(err);
      return;
    }

    const formData = new FormData();
    formData.append("document_type", documentType);
    formData.append("language", language);
    if (scope === SCOPES.customer) {
      formData.append("policy_no", policyNo.trim());
    }
    if (pdfFile) {
      formData.append("file", pdfFile);
    }

    try {
      setLoading(true);
      await updateDocument(scope, instanceToken.token, id, formData);
      homeAlert("Policy document has been updated.", false);
      navigate(`/policy-documents/${scope}/${id}`);
    } catch (error) {
      console.error(error);
      const message =
        error?.response?.data?.error || "Failed to update policy document.";
      homeAlert(message, true);
    } finally {
      setLoading(false);
    }
  };

  if (!validScope) {
    return null;
  }

  const scopeTitle = scope === SCOPES.general ? "General" : "Customer";

  return (
    <EditPageLayout
      title={`Edit ${scopeTitle} Policy Document`}
      subtitle="Update policy number, document type, or replace the PDF file."
      breadcrumbs={[
        { label: "Policy Documents", path: `/policy-documents?scope=${scope}` },
        {
          label: `Document (ID - ${id})`,
          path: `/policy-documents/${scope}/${id}`,
        },
        { label: "Edit" },
      ]}
      detailPath={`/policy-documents/${scope}/${id}`}
      listPath={`/policy-documents?scope=${scope}`}
      loading={loading || pageLoading}
      submitLabel="Update"
      onSubmit={handleSubmit}
      onRemove={handleRemove}
    >
      {doc && (
        <>
          <div className="edit-form-row-2">
            {scope === SCOPES.general ? (
              <div>
                <EditFieldLabel>Product Name</EditFieldLabel>
                <TextField fullWidth value={doc.product_type || ""} disabled />
              </div>
            ) : (
              <div>
                <EditFieldLabel required>Policy No</EditFieldLabel>
                <TextField
                  fullWidth
                  value={policyNo}
                  onChange={(e) => setPolicyNo(e.target.value)}
                  error={!!errors.policyNo}
                  helperText={errors.policyNo}
                />
              </div>
            )}
            <div>
              <EditFieldLabel>Current File</EditFieldLabel>
              <TextField fullWidth value={doc.file_name || ""} disabled />
            </div>
          </div>

          <FormControl fullWidth>
            <EditFieldLabel required>Document Type</EditFieldLabel>
            <Select
              value={documentTypePreset}
              onChange={(e) => setDocumentTypePreset(e.target.value)}
              error={!!errors.documentType}
            >
              {DOCUMENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
              <MenuItem value={CUSTOM_DOC_TYPE}>Custom...</MenuItem>
            </Select>
            {documentTypePreset === CUSTOM_DOC_TYPE && (
              <TextField
                fullWidth
                sx={{ mt: 1 }}
                placeholder="Enter custom document type"
                value={customDocumentType}
                onChange={(e) => setCustomDocumentType(e.target.value)}
                error={!!errors.documentType}
                helperText={errors.documentType}
              />
            )}
            {documentTypePreset !== CUSTOM_DOC_TYPE && errors.documentType && (
              <FormHelperText error>{errors.documentType}</FormHelperText>
            )}
          </FormControl>

          <FormControl fullWidth>
            <EditFieldLabel required>Language</EditFieldLabel>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <PolicyPdfUpload
            file={pdfFile}
            required={false}
            label="Replace PDF (optional)"
            inputId="policy-pdf-edit-input"
            error={errors.file}
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setPdfFile(file);
              setErrors((prev) => ({ ...prev, file: undefined }));
            }}
          />
        </>
      )}
    </EditPageLayout>
  );
}
